// SPDX-License-Identifier: MIT
import { t } from '../i18n/t';
import { escapeHtml } from '../lib/escape';
// v24 2026-05-17 — downloadMagicInstaller import removed (.bat magic
// installer caused Bitdefender to domain-blacklist genesis.myconciergerie.fr).
// buildMagicInstallerContent stays in forgejo.ts as dead code, ready to
// be revived behind a code-signed cert + reputation building.
import { deriveForgejoUrls, buildAuthedCloneUrl, type DerivedForgejoUrls } from '../projects/forgejo';
import { type OS, detectOS } from '../lib/os-install';

export interface ForgejoNextStepsOptions {
  htmlUrl: string;
  // Read-only token → the HTTPS clone authenticates against the login-walled
  // instance, automatic for all users (cf gitclone volet ②). null → plain URL.
  cloneToken?: string | null;
}

type Mode = 'oneliner' | 'manual';

// localStorage keys for the didactic newbie-first defaults (v20 2026-05-17).
// Visit counter increments on every panel mount ; "Mode découverte" is ON
// by default until visit count reaches NEWBIE_VISIT_THRESHOLD, then it
// auto-flips OFF with a one-shot notification. Users can override manually
// at any time — manual override sticks.
const DISCOVERY_LS_KEY = 'genesis.discoveryMode';        // '1' / '0' explicit user choice
const VISITS_LS_KEY = 'genesis.forgejoNextStepsVisits';  // numeric counter
const NEWBIE_VISIT_THRESHOLD = 3;

function getVisitCount(): number {
  try { return parseInt(localStorage.getItem(VISITS_LS_KEY) ?? '0', 10) || 0; } catch { return 0; }
}
function incrementVisitCount(): number {
  const next = getVisitCount() + 1;
  try { localStorage.setItem(VISITS_LS_KEY, String(next)); } catch { /* ignore */ }
  return next;
}
function getDiscoveryModeWithDefault(): { value: boolean; userExplicit: boolean } {
  try {
    const raw = localStorage.getItem(DISCOVERY_LS_KEY);
    if (raw === '1' || raw === '0') return { value: raw === '1', userExplicit: true };
  } catch { /* ignore */ }
  // No explicit user choice → newbie-first default : ON for the first
  // N visits, OFF after.
  return { value: getVisitCount() < NEWBIE_VISIT_THRESHOLD, userExplicit: false };
}
function setDiscoveryMode(on: boolean): void {
  try { localStorage.setItem(DISCOVERY_LS_KEY, on ? '1' : '0'); } catch { /* ignore */ }
}

// v23 2026-05-17 — Mobile detection for the handoff banner. The install
// step requires a local terminal which mobile devices can't really
// provide (no PowerShell on iOS, Termux on Android is too rough). The
// banner explains to the user to switch to PC + reassures projects sync
// via account login. Detection : either narrow viewport (≤ 700) OR
// coarse pointer (touch-primary device).
function isMobile(): boolean {
  if (typeof window === 'undefined') return false;
  if (window.innerWidth <= 700) return true;
  try {
    if (window.matchMedia('(pointer: coarse) and (hover: none)').matches) return true;
  } catch { /* matchMedia not available */ }
  return false;
}

// Transparent, AV-safe one line: clone THIS repo, enter it, launch Claude.
// (The old `irm|iex` / `curl|sh` installer was retired after the Bitdefender
// domain-blacklist incident — and prerequisites now live in Étape ①, so by the
// time the user is here, Claude Code is already installed.)
function buildOneLiner(cloneUrl: string, repoName: string): string {
  return `git clone ${cloneUrl} && cd ${repoName} && claude`;
}

// Renders the "Et maintenant ?" panel. The default mode is "oneliner" :
// one big command to copy-paste in the terminal that does everything
// (install Node + Claude Code + clone + launch). "Manual" mode (collapsible)
// is the historical 3-step path for power users.
//
// "Mode découverte" toggle (top-right) auto-opens every collapsible
// explanatory <details> + adds inline hints. Persists in localStorage.
export function renderForgejoNextSteps(opts: ForgejoNextStepsOptions): HTMLElement {
  const urls = deriveForgejoUrls(opts.htmlUrl);
  // Authed HTTPS clone (token embedded) — the clone that works for all users on
  // the login-walled instance. Falls back to the plain url when no token.
  const authedHttps = buildAuthedCloneUrl(opts.htmlUrl, opts.cloneToken);
  if (!urls.clone_url || !urls.ssh_url) {
    const fallback = document.createElement('div');
    fallback.className = 'forgejo-next-steps forgejo-next-steps--fallback';
    fallback.innerHTML = `<a href="${escapeHtml(urls.html_url)}" target="_blank" rel="noopener noreferrer">${escapeHtml(t('forgejoNext.openRepo'))} →</a>`;
    return fallback;
  }

  const root = document.createElement('section');
  root.className = 'forgejo-next-steps';
  root.setAttribute('data-component', 'forgejo-next-steps');

  const os = detectOS();
  let cloneMode: 'ssh' | 'https' = 'https'; // HTTPS default in v19+ — works without SSH key
  // v24b 2026-05-17 — default switched from 'oneliner' to 'manual' after
  // the Bitdefender domain-blacklist incident. The one-liner fetches
  // install.ps1 from genesis.myconciergerie.fr via `irm | iex` — which
  // triggers AV heuristics AND requires the domain to be reachable
  // (problematic when the domain is itself flagged). Manual mode uses
  // only standard system commands (git clone, cd, claude) that AVs
  // treat as legitimate dev activity. Server-side install.ps1 + install.sh
  // were also replaced with benign placeholder content so any future AV
  // re-scan of the URLs sees clean output.
  let mode: Mode = 'manual';

  // v20 newbie-first default : beginner explanations are expanded for the first
  // few visits, then fold. Increment the counter on mount; it gates the default
  // below. (No user-facing toggle / counter chip anymore — Carnet redesign.)
  incrementVisitCount();
  let discovery = getDiscoveryModeWithDefault().value;

  // v7.8.6 — fold state (whole block + the two themes), persisted across the
  // mode/clone/discovery re-renders that rebuild innerHTML.
  let bodyOpen = true;
  const themeOpen = [true, true];

  const applyFoldState = () => {
    const body = root.querySelector<HTMLElement>('[data-component="fns-body"]');
    if (body) body.hidden = !bodyOpen;
    const foldLabel = root.querySelector<HTMLElement>('[data-component="fns-fold-label"]');
    if (foldLabel) foldLabel.textContent = bodyOpen ? t('forgejoNext.foldCollapse') : t('forgejoNext.foldExpand');
    root.querySelector('[data-component="fns-fold"]')?.setAttribute('aria-expanded', String(bodyOpen));
    root.querySelectorAll<HTMLElement>('[data-component="fns-theme"]').forEach((th, i) => {
      th.classList.toggle('is-folded', !themeOpen[i]);
      th.querySelector('[data-component="fns-theme-head"]')?.setAttribute('aria-expanded', String(themeOpen[i]));
    });
  };

  const render = () => {
    root.setAttribute('data-mode', mode);
    root.setAttribute('data-os', os);
    root.setAttribute('data-discovery', discovery ? 'on' : 'off');
    root.innerHTML = renderMarkup(urls, os, mode, cloneMode, discovery, authedHttps);
    wire();
    applyFoldState();
  };

  const wire = () => {
    // v7.8.6 fold toggles : whole block + per-theme.
    root.querySelector<HTMLButtonElement>('[data-component="fns-fold"]')?.addEventListener('click', () => {
      bodyOpen = !bodyOpen; applyFoldState();
    });
    root.querySelectorAll<HTMLElement>('[data-component="fns-theme-head"]').forEach((head, i) => {
      const toggle = (): void => { themeOpen[i] = !themeOpen[i]; applyFoldState(); };
      head.addEventListener('click', toggle);
      head.addEventListener('keydown', (e: KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(); }
      });
    });
    // Mode switch.
    root.querySelector<HTMLButtonElement>('[data-component="mode-oneliner"]')?.addEventListener('click', () => { mode = 'oneliner'; render(); });
    root.querySelector<HTMLButtonElement>('[data-component="mode-manual"]')?.addEventListener('click', () => { mode = 'manual'; render(); });
    // SSH/HTTPS toggle (manual mode only).
    root.querySelector<HTMLButtonElement>('[data-component="clone-mode-ssh"]')?.addEventListener('click', () => { cloneMode = 'ssh'; render(); });
    root.querySelector<HTMLButtonElement>('[data-component="clone-mode-https"]')?.addEventListener('click', () => { cloneMode = 'https'; render(); });
    // Discovery toggle.
    root.querySelector<HTMLInputElement>('[data-component="discovery-toggle"]')?.addEventListener('change', (e) => {
      discovery = (e.target as HTMLInputElement).checked;
      setDiscoveryMode(discovery);
      render();
    });
    // v24 2026-05-17 — magic-installer wire removed along with the
    // button markup (AV domain-blacklist incident).
    // Copy buttons (shared across modes via data-component prefix).
    wireAllCopyButtons(root);
  };

  render();
  return root;
}

function renderMarkup(urls: DerivedForgejoUrls, os: OS, mode: Mode, cloneMode: 'ssh' | 'https', discovery: boolean, authedHttps: string): string {
  const oneLiner = buildOneLiner(authedHttps, urls.repo_name);
  const detailOpen = discovery ? 'open' : '';
  const mobile = isMobile();

  // v22 2026-05-17 — One-liner display rewritten : the operator's
  // feedback was that the long PowerShell/bash command overflows the
  // card on both mobile + desktop AND the user doesn't NEED to read
  // it, just copy it. So we hide the raw command behind a primary
  // "📋 Copier la commande" button (full-width, action-shaped) + a
  // collapsible "voir la commande exacte" details for the curious.
  // The existing wireAllCopyButtons path reads the hidden <code>'s
  // textContent so no JS plumbing changes.
  // Express = the transparent line, shown in full (no fetch-pipe, nothing
  // hidden). Prerequisites are NOT here — they live in Étape ①.
  const oneLinerSection = `
    <div class="forgejo-next-steps__oneliner">
      <p class="forgejo-next-steps__oneliner-subtitle">${escapeHtml(t('forgejoNext.oneLinerSubtitle'))}</p>
      ${codeBlock(oneLiner, 'oneliner')}
      ${terminalHelpBlock(os, detailOpen)}
    </div>
  `;

  const manualSection = `
    <ol class="forgejo-next-steps__list">
      <li class="forgejo-next-steps__step">
        <div class="forgejo-next-steps__step-label">
          <span>${escapeHtml(t('forgejoNext.step1'))}</span>
          <div class="forgejo-next-steps__clone-toggle" role="tablist" aria-label="${escapeHtml(t('forgejoNext.cloneModeAria'))}">
            <button type="button" data-component="clone-mode-https" class="forgejo-next-steps__toggle-btn" data-active="${cloneMode === 'https'}">HTTPS</button>
            <button type="button" data-component="clone-mode-ssh" class="forgejo-next-steps__toggle-btn" data-active="${cloneMode === 'ssh'}">SSH</button>
          </div>
        </div>
        ${codeBlock(`git clone ${cloneMode === 'ssh' ? urls.ssh_url : authedHttps}`, 'clone-cmd')}
        <details class="forgejo-next-steps__footnote" ${detailOpen}>
          <summary>${escapeHtml(t('forgejoNext.fnSummaryStep1'))}</summary>
          <p>${escapeHtml(t('forgejoNext.fn1'))}</p>
        </details>
        ${cloneMode === 'ssh' ? `<small class="forgejo-next-steps__hint">${escapeHtml(t('forgejoNext.sshHint'))}</small>` : ''}
      </li>
      <li class="forgejo-next-steps__step">
        <div class="forgejo-next-steps__step-label">${escapeHtml(t('forgejoNext.step2'))}</div>
        ${codeBlock(`cd ${urls.repo_name}`, 'cd-cmd')}
        <details class="forgejo-next-steps__footnote" ${detailOpen}>
          <summary>${escapeHtml(t('forgejoNext.fnSummaryStep2'))}</summary>
          <p>${escapeHtml(t('forgejoNext.fn2'))}</p>
        </details>
      </li>
      <li class="forgejo-next-steps__step">
        <div class="forgejo-next-steps__step-label">${escapeHtml(t('forgejoNext.step3'))}</div>
        ${codeBlock(`claude`, 'claude-cmd')}
        <details class="forgejo-next-steps__footnote" ${detailOpen}>
          <summary>${escapeHtml(t('forgejoNext.fnSummaryStep3'))}</summary>
          <p>${escapeHtml(t('forgejoNext.fn3'))}</p>
        </details>
        <small class="forgejo-next-steps__hint">${escapeHtml(t('forgejoNext.claudeHint'))}</small>
      </li>
    </ol>
    ${terminalHelpBlock(os, detailOpen)}
  `;

  // v23 — Mobile handoff banner. The install step can't run on a phone
  // (no local terminal). Banner explains the switch + reassures the user
  // their projects sync via account login. Always renders when mobile is
  // detected ; doesn't dismiss (would risk losing the hint mid-session).
  const mobileBanner = mobile ? `
    <div class="forgejo-next-steps__mobile-banner" data-component="mobile-banner">
      <div class="forgejo-next-steps__mobile-banner-icon" aria-hidden="true">📱→💻</div>
      <div class="forgejo-next-steps__mobile-banner-body">
        <strong>${escapeHtml(t('forgejoNext.mobileBannerTitle'))}</strong>
        <p>${t('forgejoNext.mobileBannerBody')}</p>
      </div>
    </div>
  ` : '';

  // v7.8.6 (2026-05-29) — operator brief : "la dernière partie en vert doit
  // aussi pouvoir être pliée/dépliée par section ET sous-section". The whole
  // green block folds (fns-fold on the header) ; inside, two foldable themes
  // (📦 ce que contient · 🚀 démarrer). The command/copy/mode logic is
  // untouched — only wrapped in foldable containers.
  return `
    ${mobileBanner}
    <div class="forgejo-next-steps__foldhead">
      <div class="forgejo-next-steps__header">
        <h4 class="forgejo-next-steps__title">${escapeHtml(t('ready.title'))}</h4>
        <span class="forgejo-next-steps__bravo">${escapeHtml(t('ready.bravo'))}</span>
      </div>
      <button type="button" class="forgejo-next-steps__fold-toggle" data-component="fns-fold" aria-expanded="true">
        <span data-component="fns-fold-label"></span>
      </button>
    </div>
    <div class="forgejo-next-steps__body" data-component="fns-body">
      <div class="forgejo-next-steps__theme" data-component="fns-theme">
        <div class="forgejo-next-steps__theme-head forgejo-next-steps__theme-head--toggle" data-component="fns-theme-head" role="button" tabindex="0" aria-expanded="true">
          <span>${escapeHtml(t('forgejoNext.themeContains'))}</span>
          <span class="forgejo-next-steps__theme-arrow" aria-hidden="true"></span>
        </div>
        <div class="forgejo-next-steps__theme-body">
          <p class="forgejo-next-steps__intro">${t('forgejoNext.intro').replace('{repo}', `<a href="${escapeHtml(urls.html_url)}" target="_blank" rel="noopener noreferrer" class="forgejo-next-steps__repo-link">${escapeHtml(prettyRepoName(urls.repo_name))}</a>`)}</p>
          <p class="forgejo-next-steps__equipped">${escapeHtml(t('forgejoNext.equipped'))}</p>
        </div>
      </div>
      <div class="forgejo-next-steps__theme" data-component="fns-theme">
        <div class="forgejo-next-steps__theme-head forgejo-next-steps__theme-head--toggle" data-component="fns-theme-head" role="button" tabindex="0" aria-expanded="true">
          <span>${escapeHtml(t('forgejoNext.themeStart'))}</span>
          <span class="forgejo-next-steps__theme-arrow" aria-hidden="true"></span>
        </div>
        <div class="forgejo-next-steps__theme-body">
          <div class="forgejo-next-steps__mode-switch" role="tablist" aria-label="${escapeHtml(t('forgejoNext.modeAria'))}">
            <button type="button" data-component="mode-oneliner" class="forgejo-next-steps__mode-btn" data-active="${mode === 'oneliner'}">${escapeHtml(t('forgejoNext.modeOneliner'))}</button>
            <button type="button" data-component="mode-manual" class="forgejo-next-steps__mode-btn" data-active="${mode === 'manual'}">${escapeHtml(t('forgejoNext.modeManual'))}</button>
          </div>
          ${mode === 'oneliner' ? oneLinerSection : manualSection}
          <p class="forgejo-next-steps__subnote">${escapeHtml(t('forgejoNext.subscriptionNote'))}</p>
        </div>
      </div>
      <div class="forgejo-next-steps__footer">
        <a href="${escapeHtml(urls.html_url)}" target="_blank" rel="noopener noreferrer" class="forgejo-next-steps__repo-cta">↗ ${escapeHtml(t('ready.openYourSpace'))}</a>
      </div>
    </div>
  `;
}

// Prettify a repo slug for display: drop the trailing 8-hex disambiguator,
// turn dashes into spaces, sentence-case. The real slug stays in the clone
// command — this is only the human-facing label (the raw slug intimidates a
// newbie). e.g. "club-de-petanque-a1b2c3d4" → "Club de petanque".
function prettyRepoName(slug: string): string {
  const noHash = slug.replace(/-[a-f0-9]{6,}$/i, '');
  const spaced = noHash.replace(/-+/g, ' ').trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}


// v25 2026-05-18 — Terminal help block, OS-aware. Used in both the
// oneliner section AND manual section (so newbies see it regardless
// of which mode is active). Asymmetry fixed : prior versions only
// shipped the Windows variant.
function terminalHelpBlock(os: OS, detailOpen: string): string {
  const summaryKey = os === 'windows'
    ? 'forgejoNext.howToOpenTerminal'
    : os === 'mac'
    ? 'forgejoNext.howToOpenTerminalMac'
    : 'forgejoNext.howToOpenTerminalLinux';
  const prefix = os === 'windows' ? 'winOpen' : os === 'mac' ? 'macOpen' : 'linuxOpen';
  return `
    <details class="forgejo-next-steps__detail" ${detailOpen}>
      <summary>${escapeHtml(t(summaryKey))}</summary>
      <ol class="forgejo-next-steps__detail-list">
        <li>${t(`forgejoNext.${prefix}1`)}</li>
        <li>${t(`forgejoNext.${prefix}2`)}</li>
        <li>${t(`forgejoNext.${prefix}3`)}</li>
      </ol>
    </details>
  `;
}

function codeBlock(cmd: string, dataKey: string): string {
  return `
    <div class="forgejo-next-steps__codeblock">
      <code data-component="cmd-${dataKey}">${escapeHtml(cmd)}</code>
      <button type="button" data-component="copy-${dataKey}" class="forgejo-next-steps__copy-btn" aria-label="${escapeHtml(t('forgejoNext.copyAria'))}" data-state="idle">📋</button>
    </div>
  `;
}

function wireAllCopyButtons(root: HTMLElement): void {
  // Skip elements whose data-component starts with "copy-label-" —
  // those are inner spans for the primary copy button (the label
  // mutates on state change), not actual click targets.
  root.querySelectorAll<HTMLButtonElement>('[data-component^="copy-"]:not([data-component^="copy-label-"])').forEach((btn) => {
    btn.addEventListener('click', async () => {
      const key = btn.getAttribute('data-component')!.replace(/^copy-/, '');
      const code = root.querySelector<HTMLElement>(`[data-component="cmd-${key}"]`);
      if (!code) return;
      const text = code.textContent ?? '';
      const ok = await copyText(text);
      // For the primary copy button (one-liner) we mutate the label,
      // not the button's whole content (we want to keep the icon +
      // hint layout). Other copy buttons keep the old icon-only swap.
      const labelEl = root.querySelector<HTMLElement>(`[data-component="copy-label-${key}"]`);
      btn.setAttribute('data-state', ok ? 'copied' : 'error');
      if (labelEl) {
        const original = labelEl.textContent ?? '';
        labelEl.textContent = ok ? t('forgejoNext.copiedFlash') : t('forgejoNext.copyError');
        setTimeout(() => {
          btn.setAttribute('data-state', 'idle');
          labelEl.textContent = original;
        }, 1800);
      } else {
        const original = btn.textContent ?? '📋';
        btn.textContent = ok ? '✓' : '✗';
        setTimeout(() => {
          btn.setAttribute('data-state', 'idle');
          btn.textContent = original.length <= 2 ? '📋' : original;
        }, 1600);
      }
    });
  });
}

async function copyText(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch { /* fall through */ }
  const ta = document.createElement('textarea');
  ta.value = text;
  ta.style.position = 'fixed';
  ta.style.opacity = '0';
  document.body.appendChild(ta);
  ta.select();
  let ok = false;
  try { ok = document.execCommand('copy'); } catch { ok = false; }
  document.body.removeChild(ta);
  return ok;
}
